import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { JsonConfiguration } from './models/json';
import { Title } from '../../../shared/title/title.component';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { ScreenDeviceService } from '../../../core/services/Screen/screen-device.service';
import { HeaderConfiguration } from '../../../shared/header/header.component';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomValidators } from '../../../shared/validators/custom-validators';
import { getEmailSuggestion } from '../../../shared/utils/email-utils/email-utils';
import { AnalyticsService } from '../../../core/services/adobe-analytics/analytics.service';
import { environment } from '../../../../environments/environment';
import { Utils } from '../../../shared/utils/utils';

const DATE_FORMAT_DDMMYYYY = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-data-info',
  templateUrl: './data-info.component.html',
  styleUrls: ['./data-info.component.scss'],
  providers: [
    JsonConfiguration,
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT_DDMMYYYY },
  ]
})
export class DataInfoComponent implements OnInit {

  @Output() complete = new EventEmitter<void>();
  @Output() backStep = new EventEmitter<number>();
  @Output() formData = new EventEmitter<void>();

  personalForm!: FormGroup;
  titleData!: Title;
  whiteHandIcon: string = 'assets/img/Mano-blanca.svg';
  destroy$ = new Subject<void>();
  isDesktopScreen: boolean = false;
  formFieldsArray = {
    name: 'direccion',
    formControlName: 'direccion',
    label: 'Dirección',
    placeholder: 'Introduce tu dirección',
    type: 'google',
    options: []
  };
  fieldsArr: any[] = [this.formFieldsArray];
  headerConfiguration: HeaderConfiguration;
  step: number = 1;
  maxDate: Date;
  isContinueDisabled: boolean = true;
  emailSuggestion: string | null = null;

  constructor(
    private fb: FormBuilder,
    private screenDeviceService: ScreenDeviceService,
    private jsonConfiguration: JsonConfiguration,
    private cdRef: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
  ) {}

  ngOnInit(): void {
    this.analyticsService.resetDataLayer();
    this.createForm();
    this.restoreFormFromSession();
    this.checkScreen();
    this.set_header_configuration();
    this.titleData = this.jsonConfiguration.titleDataXsell;
    this.calculateMaxDate();
    
    this.set_initial_analytics_config('',
      'imPageview', 
      'app_cotizador:area_publica:sales:personalData', 
      'personalData', 
      'sales_personalData', 
      '8');

    this.analyticsService.startEventWithCallback('imPageview', () => {});

    this.personalForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.isContinueDisabled = status !== 'VALID';
        this.cdRef.detectChanges();
      });

    this.personalForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        sessionStorage.setItem('personalForm', JSON.stringify(value));
      });

    this.personalForm.get('Email')?.valueChanges
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(value => {
        this.emailSuggestion = getEmailSuggestion(value);
      });

    window.scrollTo(0, 0);
  }

  calculateMaxDate() {
    const today = new Date();
    this.maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  }

  createForm() {
    this.personalForm = this.fb.group({
      dni: ['', [Validators.required, CustomValidators.checkDniNie]],
      nombre: ['', Validators.required],
      apellidos: ['', Validators.required],
      fechaNacimiento: ['', Validators.required],
      direccion: ['', [Validators.required, Validators.pattern(/^.+,\s\d+,\s.+,\s\d{5}$/)]],
      Email: ['', [Validators.required, Validators.email]],
      Telefono: ['', [Validators.required, Validators.maxLength(9), Validators.pattern(/^\d{9}$/)]]
    });
  }

  restoreFormFromSession(): void {
    const savedForm = sessionStorage.getItem('personalForm');
    if (savedForm) {
      const parsed = JSON.parse(savedForm);
      Object.keys(parsed).forEach(key => {
        if (this.personalForm.contains(key)) {
          this.personalForm.get(key)?.setValue(parsed[key]);
        }
      });
    }
  }

  checkScreen() {
    this.screenDeviceService.checkScreenSize()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.isDesktopScreen = result.matches;
      });
  }

  onSubmit(): void {
    if (!this.personalForm.invalid) {

      this.set_initial_analytics_config(
        'click-button:continuar',
        'SalesP2',
        'app_cotizador:area_publica:sales:personalData', 
        'yourInsurance', 
        'sales_yourInsurance', 
        '3'
      );

      this.analyticsService.startEventWithCallback('SalesP2', () => {
        this.formData.emit(this.personalForm.value);
        this.complete.emit();
      });

    } else {
      this.personalForm.markAllAsTouched();
    }
  }

  handleBackClick() {
    this.backStep.emit(this.step);
  }

  applyEmailSuggestion() {
    if (this.emailSuggestion) {
      this.personalForm.get('Email')?.setValue(this.emailSuggestion);
      this.emailSuggestion = null;
    }
  }

  private set_header_configuration() {
    this.headerConfiguration = {
      layoutAlign: 'start start',
      sections: [
        { backButton: { styles: ['fs_4_underline'] } }
      ]
    };
  }

  /**
    * Set all the analytics' related info for the current component
  */
  private set_initial_analytics_config(categoryActionLabel: string, eventName: string, pageName: string, familySelection: string, processName: string, processStep: string): void {
    const sessionData = this.getAnalyticsDataFromSession();
    this.analyticsService.resetDataLayer();
    /* ===================== */
    /* PAGE DATA */
    /* ===================== */
    this.analyticsService.setPhysicalUrl(environment.baseUrl + sessionData.aliasProducto);
    this.analyticsService.setPageName(pageName);
    this.analyticsService.setPageContentType('cotizador');
    this.analyticsService.setPageDigitalChannel('app_cotizador');
    this.analyticsService.setPageSection('area_publica');
    this.analyticsService.setPageLanguage('es');
    this.analyticsService.setNivel1('sales');
    this.analyticsService.setNivel2(familySelection);
    /* ===================== */
    /* PROCESS DATA */
    /* ===================== */
    this.analyticsService.setProcessStep(processStep);
    this.analyticsService.setProcessType('sales');
    this.analyticsService.setProcessName(processName);
    this.analyticsService.setProcessStatus('iniciado');

    /* ===================== */
    /* USER DATA */
    /* ===================== */
    this.analyticsService.setUserStatus('no_logado');
    this.analyticsService.setUserLang('es');
    const personalFormData = sessionStorage.getItem('personalForm');
    let userBirth = '';
    if (personalFormData) {
      try {
        const parsed = JSON.parse(personalFormData);
        // Aquí cogemos la fecha tal cual viene
        userBirth = parsed.fechaNacimiento ?? '';
      } catch (err) {
        console.warn('Error parsing personalForm from sessionStorage', err);
      }
    }

    this.analyticsService.setUserBirth(userBirth);
    this.analyticsService.setUserResidence('VERDADERO');

    /* ===================== */
    /* PRODUCT DATA (DINÁMICO) */
    /* ===================== */

    this.analyticsService.setFamilySelection(familySelection)
    this.analyticsService.setCategoryActionLabel(categoryActionLabel)
    this.analyticsService.setProductName(Utils.sanitizeString(sessionData.productName));
    this.analyticsService.setProductType(Utils.sanitizeString(sessionData.productType));
    this.analyticsService.setProductArea(sessionData.productArea);
    this.analyticsService.setProductDate(Utils.getProductDate());
    this.analyticsService.setProductPack('todo el año')

    const productCostStart = sessionData.productCostStart;
    const productCostUpgrades = sessionData.productCostUpgrades;
    this.analyticsService.setProductCostStart(productCostStart);
    this.analyticsService.setProductCostUpgrades(productCostUpgrades);
  }

  private getAnalyticsDataFromSession(): any {
    const stored = sessionStorage.getItem('analyticsData');
    if (!stored) return null;
    return JSON.parse(stored);
  }

}
